import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GetListAsync } from "../../providers/dataProvider";
import { useRolePermission } from "../../hooks/useRolePermission";
import PaginationComponent from "../../components/common/PaginationComponent";
import { formatActivityDate } from "./activityFormatting";
import "./QuotationActivity.scss";

const QuotationActivity = () => {
  const { quotationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { menuRights, menuRightsLoading } = useRolePermission("Quotation", "canViewActivities");
  const [activities, setActivities] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(() => location.state?.pageNumber || 1);
  const [pageSize, setPageSize] = useState(() => location.state?.pageSize || 25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
      detail: {
        pathname: "/quotation-activities/" + quotationId,
        title: location.state?.quoteReference
          ? `Activity · ${location.state.quoteReference}`
          : "Quotation Activity",
        backTarget: location.state?.backTarget || { pathname: "/quotation" },
      },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: "/quotation-activities/" + quotationId,
          clear: true,
        },
      }));
    };
  }, [location.state, quotationId]);

  useEffect(() => {
    if (menuRightsLoading || menuRights?.canViewActivities !== true) return undefined;

    let isCurrentRequest = true;
    setLoading(true);
    setError("");

    GetListAsync("quotationActivities", {
      quotationId,
      pageNumber,
      pageSize,
      includeDetails: false,
    }).then((result) => {
      if (!isCurrentRequest) return;

      if (!result || result.message !== "success") {
        const errorMessage = typeof result?.data === "string"
          ? result.data
          : result?.data?.message || "Unable to load quotation activity.";
        setError(errorMessage);
        setActivities([]);
        setTotalRecords(0);
        return;
      }

      const response = result.data;
      setActivities(Array.isArray(response?.payload) ? response.payload : []);
      setTotalRecords(Number(response?.totalRecords) || 0);
    }).catch((requestError) => {
      if (!isCurrentRequest) return;
      setError(requestError?.message || "Unable to load quotation activity.");
      setActivities([]);
      setTotalRecords(0);
    }).finally(() => {
      if (isCurrentRequest) setLoading(false);
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [quotationId, pageNumber, pageSize, menuRights?.canViewActivities, menuRightsLoading]);

  const paginationData = { totalRecords, payload: activities };
  const openActivityDetails = (activity) => {
    navigate(`/quotation-activities/${encodeURIComponent(quotationId)}/details/${encodeURIComponent(activity.id)}`, {
      state: {
        activity,
        quoteReference: location.state?.quoteReference,
        backTarget: {
          pathname: `/quotation-activities/${encodeURIComponent(quotationId)}`,
          state: {
            quoteReference: location.state?.quoteReference,
            pageNumber,
            pageSize,
          },
        },
      },
    });
  };

  return (
    <main className="container-fluid quotation-activity-page">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <section className="quotation-activity-card" aria-label="Quotation activity">
        <div className="quotation-activity-heading">
          <div>
            <h2>Activity history</h2>
            <p>Changes and actions recorded for this quotation.</p>
          </div>
          <span className="quotation-activity-count">
            {totalRecords.toLocaleString()} {totalRecords === 1 ? "event" : "events"}
          </span>
        </div>

        {loading ? (
          <div className="quotation-activity-state" role="status">Loading activity...</div>
        ) : activities.length > 0 ? (
          <div className="table-responsive">
            <table className="table quotation-activity-table">
              <thead>
                <tr>
                  <th scope="col">Date and time</th>
                  <th scope="col">Activity</th>
                  <th scope="col">User</th>
                  <th scope="col">Change</th>
                  <th scope="col">Entity</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => (
                  <tr key={activity.id}>
                    <td className="activity-date">{formatActivityDate(activity.occurredAtUtc)}</td>
                    <td>
                      <strong>{activity.activityTitle || activity.changeType || "Activity"}</strong>
                      <span className="activity-description">
                        {activity.activityDescription || activity.activity}
                      </span>
                    </td>
                    <td>{activity.userName || "-"}</td>
                    <td>{activity.changeType || "-"}</td>
                    <td>{activity.entityName || "-"}</td>
                    <td>{activity.activityReference || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="quotation-activity-view-details"
                        onClick={() => openActivityDetails(activity)}
                      >
                        <i className="fas fa-caret-right" aria-hidden="true" />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="quotation-activity-state">
            {error ? "Activity could not be loaded." : "No activity has been recorded for this quotation."}
          </div>
        )}
      </section>

      {totalRecords > 0 && (
        <PaginationComponent
          moduleName="Quotation"
          count={pageSize}
          setCount={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPageNumber(1);
          }}
          pageSize={pageSize}
          currentPage={pageNumber}
          filteredResults={paginationData}
          setCurrentPage={setPageNumber}
        />
      )}
    </main>
  );
};

export default QuotationActivity;
